export const POSTCODE_AREAS_GROUP = 'Postcodes';
export const ADDRESSES_GROUP = 'Addresses';
export const MIN_POSTCODE_AREA_QUERY_LENGTH = 2;
export const MAX_POSTCODE_AREA_OPTIONS = 4;
export const MAX_ADDRESS_OPTIONS_WHEN_SECTIONED = 4;

const POSTCODE_RESOLVE_ENDPOINT = '/Postcodes/Resolve';
const MIN_QUOTABLE_POSTCODE_LENGTH = 2;

export interface PostcodeAreaOption {
  id: string;
  label: string;
  secondaryLabel?: string;
  groupLabel: string;
  postcodeArea: string;
  lat: number;
  lon: number;
}

export interface AddressSuggestionOption {
  id: string;
  label: string;
  city?: string;
  country?: string;
  groupLabel?: string;
}

export type TransportSuggestionOption =
  | PostcodeAreaOption
  | AddressSuggestionOption;

interface PostcodeResolveCandidate {
  matchedPrefix: string;
  placeName: string | null;
  lat: number;
  lon: number;
}

interface PostcodeResolveResult {
  input: string;
  status: 'exact' | 'prefix' | 'not_found';
  candidates?: PostcodeResolveCandidate[];
}

interface PostcodeResolveResponse {
  results?: PostcodeResolveResult[];
}

interface AuthHttpClient {
  post<T>(url: string, body: unknown): Promise<{ data: T }>;
}

// The resolver walks the typed prefix down to a single character, but the quote
// engine refuses postcodes shorter than 2 (PostcodePrefix.PrefixCandidates), so a
// single-letter pick would silently produce zero quotes. Coordinates of exactly
// 0,0 are the backend's null fallback, never a real UK location.
function isQuotableCandidate(candidate: PostcodeResolveCandidate): boolean {
  const prefix = candidate?.matchedPrefix?.trim() ?? '';
  if (prefix.length < MIN_QUOTABLE_POSTCODE_LENGTH) return false;
  if (!Number.isFinite(candidate.lat) || !Number.isFinite(candidate.lon)) {
    return false;
  }
  return !(candidate.lat === 0 && candidate.lon === 0);
}

function describePlace(
  candidate: PostcodeResolveCandidate
): string | undefined {
  const place = candidate.placeName?.trim();
  if (!place || place === candidate.matchedPrefix) return undefined;
  return place;
}

export function toPostcodeAreaOptions(
  response: PostcodeResolveResponse | null | undefined
): PostcodeAreaOption[] {
  const candidates = response?.results?.[0]?.candidates ?? [];
  return candidates
    .filter(isQuotableCandidate)
    .slice(0, MAX_POSTCODE_AREA_OPTIONS)
    .map((candidate) => ({
      id: `postcode-area:${candidate.matchedPrefix}:${
        candidate.placeName ?? ''
      }`,
      label: candidate.matchedPrefix,
      secondaryLabel: describePlace(candidate),
      groupLabel: POSTCODE_AREAS_GROUP,
      postcodeArea: candidate.matchedPrefix,
      lat: candidate.lat,
      lon: candidate.lon,
    }));
}

export async function fetchPostcodeAreaOptions(
  authHttp: AuthHttpClient,
  input: string
): Promise<PostcodeAreaOption[]> {
  const query = input.trim();
  if (query.length < MIN_POSTCODE_AREA_QUERY_LENGTH) return [];
  try {
    const { data } = await authHttp.post<PostcodeResolveResponse>(
      POSTCODE_RESOLVE_ENDPOINT,
      {
        postcodes: [query],
        countryCode: 'GB',
        maxCandidatesPerInput: MAX_POSTCODE_AREA_OPTIONS,
      }
    );
    return toPostcodeAreaOptions(data);
  } catch {
    return [];
  }
}

export function mergeSuggestionSections(
  areaOptions: PostcodeAreaOption[],
  addressOptions: AddressSuggestionOption[]
): TransportSuggestionOption[] {
  if (areaOptions.length === 0) return addressOptions;
  const sectionedAddresses = addressOptions
    .slice(0, MAX_ADDRESS_OPTIONS_WHEN_SECTIONED)
    .map((option) => ({ ...option, groupLabel: ADDRESSES_GROUP }));
  return [...areaOptions, ...sectionedAddresses];
}
